// IRSLimitDial — screen-specific radial dial for the 2026 IRS contribution
// limit. The reusable ArcGauge is a single-arc semicircle; this is a full-circle
// DUAL-arc dial (solid "contributed YTD" ring + a dashed "projected at new rate"
// ring that recolors when the projection would hit the cap). Domain-shaped, so
// it stays in the module (not promoted to reusable). Token-colored, presentational.
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const R = 45;
const ARC_LEN = 2 * Math.PI * R; // ≈ 282.74

export function IRSLimitDial({ contributedYtd, projected, limit, willCap = false }) {
  const { t } = useTranslation();
  const ytdPct = Math.min(100, (contributedYtd / limit) * 100);
  const projPct = projected != null ? Math.min(100, (projected / limit) * 100) : null;
  const showProjected = projPct != null && Math.round(projPct) !== Math.round(ytdPct);

  const dash = (pct) => `${(pct / 100) * ARC_LEN} ${ARC_LEN}`;

  return (
    <svg viewBox="0 0 120 120" className="ma-dial" role="img" aria-label={t('manage.contributions.dialAria', { pct: Math.round(ytdPct) })}>
      {/* Track */}
      <circle cx="60" cy="60" r={R} stroke="var(--bg-sunken)" strokeWidth="14" fill="none" />
      {/* Projected ring (under the YTD ring) — purple when it would cap, else blue. */}
      {showProjected && (
        <circle
          cx="60"
          cy="60"
          r={R}
          stroke={willCap ? 'var(--brand-purple)' : 'var(--brand-blue)'}
          strokeOpacity={willCap ? 0.55 : 0.32}
          strokeWidth="14"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={dash(projPct)}
          transform="rotate(-90 60 60)"
          style={{ transition: 'stroke-dasharray .6s cubic-bezier(.4,0,.2,1)' }}
        />
      )}
      {/* Contributed-to-date ring (on top). */}
      <circle
        cx="60"
        cy="60"
        r={R}
        stroke="var(--brand-blue)"
        strokeWidth="14"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={dash(ytdPct)}
        transform="rotate(-90 60 60)"
        style={{ transition: 'stroke-dasharray .6s cubic-bezier(.4,0,.2,1)' }}
      />
      <text x="60" y="58" textAnchor="middle" className="ma-dial__pct">
        {Math.round(ytdPct)}%
      </text>
      <text x="60" y="74" textAnchor="middle" className="ma-dial__cap">
        {t('manage.contributions.ofLimit')}
      </text>
    </svg>
  );
}

IRSLimitDial.propTypes = {
  contributedYtd: PropTypes.number.isRequired,
  /** Projected year-end contribution at the simulated rate; null = no preview. */
  projected: PropTypes.number,
  limit: PropTypes.number.isRequired,
  /** True when the projection reaches the IRS limit before year-end. */
  willCap: PropTypes.bool,
};

export default IRSLimitDial;
